import { Project } from 'types/types';

export type ProjectField = keyof Project;

export type ProjectPatch = Partial<Project> & { id: number };

// Which fields each wizard step is responsible for. A step's save sends these and
// nothing else, so a value the backend rejects on one step cannot make every later
// step's save fail too (ID 7 root cause 2, ID 9 root cause 4).
//
// Keys are the step numbers used in the /add-project/<step> routes. Fields must not be
// repeated across steps - a step would otherwise overwrite a saved value with the stale
// copy its own form happened to hold. `lca_tool` and `ECOM_version` are absent on
// purpose: only the spreadsheet import writes them.
export const STEP_FIELDS: Record<number, readonly ProjectField[]> = {
  // 1 - Project Information. `firm` is only present for a Super Admin, who picks it here.
  1: [
    'firm',
    'name',
    'address',
    'country',
    'subnational',
    'city',
    'zipcode',
    'primary_building_use_type',
    'construction_type',
    'construction_completion_year',
    'gross_area_sqft',
    'mean_roof_height_ft',
    'stories_above_grade',
    'stories_below_grade',
  ],

  // 2 - Structural System
  2: [
    'tcg_long_direction_ft',
    'risk_category',
    'typical_floor_live_load_psf',
    'ground_snow_load_psf',
    'ultimate_wind_speed_mph',
    'seismic_design_category',
    'primary_horizontal_gravity_system',
    'primary_vertical_gravity_system',
    'primary_lateral_system',
    'podium',
    'foundation_type',
    'allowable_soil_bearing_pressure_psf',
  ],

  // 3-5 - Substructure / Superstructure quantities. All three edit the same smqs array.
  3: ['smqs'],
  4: ['smqs'],
  5: ['smqs'],

  // 6 - Structural Material Quantity assumptions
  6: [
    'material_quantity_data_source',
    'steel_connection_allowances',
    'rebar_allowances',
    'concrete_waste_allowances',
    'carbon_factor_data_source',
    'other_carbon_factor_data_source',
    'additional_assumptions',
  ],

  // 7 - Embodied Carbon Assessment
  7: [
    'other_lca_tool',
    'lca_year',
    'lca_project_phase',
    'ref_study_years',
  ],

  // 8 - Embodied Carbon Reporting
  8: [
    'a1_a3_gwp_kg',
    'a4_gwp_kg',
    'a5_gwp_kg',
    'b1_b5_gwp_kg',
    'c1_c4_gwp_kg',
    'd_gwp_kg',
    'biogenic_carbon_flow_kg',
  ],
};

// Sent on every save regardless of step. `is_submitted` is deliberately absent - see
// buildProjectPatch.
const ALWAYS_SENT: readonly ProjectField[] = ['id'];

interface BuildPatchProps {
  projectDetails: Project;
  formValues?: Partial<Project>;
  // Omit to send the whole project. Submit does that on purpose: the backend's
  // deep-foundation rule keys off foundation_type being present in the request
  // (serializers.py:315-317), so a narrowed payload would skip that check.
  fields?: readonly ProjectField[];
}

export const buildProjectPatch = ({
  projectDetails,
  formValues = {},
  fields,
}: BuildPatchProps): ProjectPatch => {
  // `is_submitted` is never emitted here. On an update, omitting it makes the backend
  // read submission state off the stored row (serializers.py:305) instead of trusting
  // whatever context happens to hold - a stale `false` is what reverted a submitted
  // project to Draft in Entry ID 12 Bug 2. The two payloads that legitimately know the
  // value, create and submit, set it explicitly in useCreateUpdateProject.
  const merged = { ...projectDetails, ...formValues } as Record<string, unknown>;
  delete merged.is_submitted;

  if (!fields) return merged as unknown as ProjectPatch;

  const patch = [...ALWAYS_SENT, ...fields].reduce((accumulated, field) => {
    if (field in merged) {
      accumulated[field] = merged[field];
    }

    return accumulated;
  }, {} as Record<string, unknown>);

  return patch as ProjectPatch;
};

// Drops values the backend rejected so the next save is not poisoned by them. Sets them
// to null rather than deleting the key, matching the context's empty-project shape.
export const clearRejectedFields = (projectDetails: Project, rejected: string[]): Project => {
  const clearable = rejected.filter(field => field !== 'smqs' && field in projectDetails);

  if (!clearable.length) return projectDetails;

  const cleared = clearable.reduce((accumulated, field) => {
    accumulated[field] = null;

    return accumulated;
  }, { ...projectDetails } as unknown as Record<string, unknown>);

  return cleared as unknown as Project;
};
